import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, catchError, firstValueFrom, map, of } from 'rxjs';

import { environment } from '../../../../environments/environment';
import { CornerstoneApiResponse } from '../../../shared/models/http.model';
import {
  Store,
  StoreConfig,
  StoreCreateRequest,
} from '../../../shared/models/store.model';
import { ErrorHandlerService } from '../../../shared/services/error-handler.service';
import { HttpService } from '../../../shared/services/http.service';
import { SubscriptionWrappers } from '../../../shared/services/subscription-wrappers';

const API_URL = environment.apiUrl;
const NEST_API_URL = environment.nestApiUrl;

@Injectable({
  providedIn: 'root',
})
export class StoreService implements OnDestroy {
  public _store = new Subject<Store>();
  public store$ = this._store.asObservable();
  public subwrapper = new SubscriptionWrappers();

  constructor(
    private readonly httpService: HttpService,
    private readonly httpClient: HttpClient,
    private readonly errorHandler: ErrorHandlerService
  ) {}

  ngOnDestroy(): void {
    this.subwrapper.cleanup();
  }
  public createStore(store: StoreCreateRequest): Observable<Store> {
    let body = {
      ...store,
    };
    return this.httpClient
      .post<CornerstoneApiResponse<Store>>(
        NEST_API_URL + '/store',
        body,
        this.httpService.getHttpOptions()
      )
      .pipe(
        map((res) => res.data),
        catchError((error: HttpErrorResponse) => {
          this.errorHandler.handleError(error);
          throw error;
        })
      );
  }

  notifyStore(store: Store) {
    const _store = new Store();
    _store.setStore(store);
    this._store.next(_store);
  }

  public getStore(storeId: string): Observable<Store> {
    return this.httpClient
      .get<CornerstoneApiResponse<Store>>(
        `${NEST_API_URL}/store/${storeId}`,
        this.httpService.getHttpOptions()
      )
      .pipe(
        map((res) => res.data),
        catchError((error: HttpErrorResponse) => {
          this.errorHandler.handleError(error);
          throw error;
        })
      );
  }

  /**
   * Update the configuration for the specified store
   * @param storeId store id of the store to update
   * @param config new configuration for the store
   * @returns Observable of response
   */
  public updateConfig(storeId: string, config: StoreConfig): Observable<Store> {
    return this.httpClient
      .put<CornerstoneApiResponse<Store>>(
        `${NEST_API_URL}/store/${storeId}/config`,
        config,
        this.httpService.getHttpOptions()
      )
      .pipe(
        map((res) => {
          this.notifyStore(res.data);
          return res.data;
        }),
        catchError((error: HttpErrorResponse) => {
          this.errorHandler.handleError(error);
          throw error;
        })
      );
  }

  public createFulfillmentService(storeId: string) {
    return this.httpClient
      .post<CornerstoneApiResponse<Store>>(
        `${NEST_API_URL}/store/${storeId}/fulfillment-service`,
        {
          storeId,
        },
        this.httpService.getHttpOptions()
      )
      .pipe(
        map((res) => {
          this.notifyStore(res.data);
          return res.data;
        }),
        catchError((error: HttpErrorResponse) => {
          this.errorHandler.handleError(error);
          throw error;
        })
      );
  }

  public getShopifyRedirectLink(shop: string): Promise<string> {
    return firstValueFrom(
      this.httpClient
        .get<CornerstoneApiResponse<string>>(
          `${API_URL}/stores/shopify/redirect?shop=${shop}`,
          this.httpService.getHttpOptions()
        )
        .pipe(map((res) => res.data))
    );
  }

  /**
   * Set the access token for the specified store
   * @param storeId store id of the store to update
   * @param token new access token for the store
   * @returns Observable of response
   */
  public setAccessToken(storeId: string, token: string): Observable<Store> {
    return this.httpClient
      .post<CornerstoneApiResponse<Store>>(
        `${NEST_API_URL}/store/${storeId}/access-token`,
        { access_token: token },
        this.httpService.getHttpOptions()
      )
      .pipe(
        map((res) => {
          this.notifyStore(res.data);
          return res.data;
        }),
        catchError((error: HttpErrorResponse) => {
          this.errorHandler.handleError(error);
          throw error;
        })
      );
  }

  public deleteStore(uuid): Observable<any> {
    return this.httpClient
      .delete<any>(
        NEST_API_URL + '/store/' + uuid,
        this.httpService.getHttpOptions()
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.errorHandler.handleError(error);
          return of(error);
        })
      );
  }

  public generateLink(uid: string): Observable<string> {
    return this.httpClient
      .get<CornerstoneApiResponse<string>>(
        `${NEST_API_URL}/store/${uid}/generate-link`,
        this.httpService.getHttpOptions()
      )
      .pipe(
        map((res) => res.data),
        catchError((error: HttpErrorResponse) => {
          this.errorHandler.handleError(error);
          throw error;
        })
      );
  }
  
  /**
   * Get Sam's Club shipnodes for a store
   * @param storeId The store ID
   * @returns Observable of shipnodes array
   */
  public getSamsClubShipnodes(storeId: string): Observable<{ shipNodeId: string, shipNodeName: string }[]> {
    return this.httpClient
      .get<CornerstoneApiResponse<{ shipNodeId: string, shipNodeName: string }[]>>(
        `${NEST_API_URL}/store/${storeId}/sams-club/shipnodes`,
        this.httpService.getHttpOptions()
      )
      .pipe(
        map((res) => res.data),
        catchError((error: HttpErrorResponse) => {
          this.errorHandler.handleError(error);
          throw error;
        })
      );
  }
}
